import { Box, List, Divider, ListItemButton, ListItemText, Typography, TextField, Stack, ListItemIcon, styled, Button, Grid,
  OutlinedInput, Card, IconButton, Input, FormGroup, Switch, FormControlLabel, Paper, Tooltip, FormHelperText, Dialog, DialogTitle,
  DialogContent, DialogActions, GlobalStyles, ButtonBase, SxProps, Theme,
  useTheme,
  darken} from "@mui/material";
import StarIcon from "@mui/icons-material/Star"
import RemoveIcon from '@mui/icons-material/Remove';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import { ChangeEvent, FC, useCallback, useMemo, useState, useEffect, useRef } from "react";
import Editor from "react-simple-code-editor";
import numeral from 'numeral';
import { AddSubsetMasterTablesInput, AddSubsetMasterTablesResponseDto, DatabaseResponseDto, SubsetMasterTableInput, SubsetMasterTableResponseDto, TableSchemaAndNameDto,
  TableWithRefsResponseDto, UpdateDatabaseSubsetLimitInput } from "@/gen/api/encrypt";
import Prism from 'prismjs';
import 'prismjs/components/prism-sql'; // SQL language support
import prismCss from './prism.css?inline';
import prismDarkCss from './prism-dark.css?inline';
import * as Yup from 'yup';
import { Content } from "../content/Content";
import { OdResourcePageProps } from "@/types/resource";
import { useDatabaseSelectCard, useEncryptableDatabasesSource } from "../databases/DatabaseSelectCard";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid";
import { ShowBase, useGetOne, useShowContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import Create from "@/controller/Create";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATABASE_OPERATIONS } from "@/constants";
import { disabledColor } from "@/utils/a11y";
import { nonSqlDbTypes } from "./defs";
import { EncryptionMappingResponseDto } from "@/gen/api/governance";
import { Redirect } from "react-router-dom";
import { useEventBus } from "@/contexts/EventBusContext/useEventBus";
import { useCreateZTreeStore } from "@/utils/zustand-form/store";
import { FormNode, FormToken, FormUtils, useZSliceUnderContext, ZTreeStoreProvider } from "@/utils/zustand-form";
import { CollectEditsMetadata, CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";
import { usePlainStateFormUtils } from "../input/usePlainStateFormUtils";
import WithSave from "@/controller/WithSave";
import { useSnackbar } from "notistack";
import { debounce } from "lodash";


const DbSubsettingDetail: FC<OdResourcePageProps & { id?: string}> = (props) => {
  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '')
  const dispatch = useDispatch()

  const handleSwitchDatabase = useCallback((db: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/encryption/subsetting/mastertables/$0/show`, db.databaseId || 0, db.name)))
  }, [dispatch])

  const theme = useTheme()
  useEffect(() => {
    // Inject CSS only for this component to avoid global pollution
    const styleEl = document.createElement('style');
    styleEl.textContent = theme.palette.mode === 'light' ? prismCss : prismDarkCss;
    document.head.appendChild(styleEl);

    return () => {
      styleEl.parentNode?.removeChild(styleEl);
    };
  }, [theme.palette.mode]); 

  return (
    <>
      <GlobalStyles 
        styles={(theme) => ({
          'body div.scroll-host > div.MuiBox-root': {
            paddingBottom: 'unset',
          }
        })}
      />
      <DbSubsettingDetailBody onSwitchDatabase={handleSwitchDatabase} {...{id}} {...rest} />
    </>
  )
}

interface DbSubsettingDetailBodyProps extends OdResourcePageProps {
  onSwitchDatabase: (x: DatabaseResponseDto) => void;
  id: number;
}

const DbSubsettingDetailBody: FC<DbSubsettingDetailBodyProps> = ({
  id,
  onSwitchDatabase,
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const { data: dbTables } = useGetOne<{data:TableWithRefsResponseDto[]}&RecPatch>('subset-tablerefs', id)
  const candidates = useMemo(() => {
    return getMasterTableCandidates(dbTables?.data ?? [])
  }, [dbTables?.data])

  //@ts-ignore
  const dbs = useEncryptableDatabasesSource(true)
  const useDataSource = () => useMemo(() => dbs.filter(db => !nonSqlDbTypes.has((db as EncryptionMappingResponseDto).databaseType!)), [dbs])
  const DatabaseSelectCard = useDatabaseSelectCard(useDataSource)

  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      onSwitchDatabase(chosenDb)
    }
  }, [onSwitchDatabase])

  const dbLimitHandler = useDefaultSavedHandlers("Save Database Subset Limit")
  const masterTableHandler = useDefaultSavedHandlers("Save Master Tables Setting")

  const currDb = dbs.find(db => db.databaseId === id) as DatabaseResponseDto|undefined
  if (dbs.length > 0 && (!currDb || nonSqlDbTypes.has(currDb.databaseType!))) {
    return <Redirect to="/encryption/dashboard" />
  }

  return (
    <Content
      pageName="Database Subsetting Config"
      resourceName="Subsetting Config"
      breadcrumbs={options?.breadcrumbs}
    >

      <Grid container spacing={4} 
        sx={{ 
          ...(!hasWritePermit && { '& input.Mui-disabled': disabledColor }),
        }}
      >
        <Grid item md={6} xs={12} >
          <DatabaseSelectCard preSelectDbId={id} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} >
          <Card>
            <Create resource="subset-database-default-limit" basePath="" {...dbLimitHandler}>
              <DatabaseSubsetLimtEditor databaseId={id} />
            </Create>
            <Divider />
            <Create resource="encryption/subsetting/mastertables" basePath="" onFailure={masterTableHandler.onFailure}>
              <ShowBase resource="encryption/subsetting/mastertables" id={id} basePath="">
                <MasterTablesEditor databaseId={id} candidate={candidates} />
              </ShowBase>
            </Create>
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default DbSubsettingDetail;


const MasterTablesEditor : FC<{candidate: CalculatedCandidates, databaseId: number}> = ({
  candidate,
  databaseId,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const eventBus = useEventBus('MasterTablesEditor')
  
  const { record: prevSettings } = useShowContext<{data:SubsetMasterTableResponseDto[]}&RecPatch>();

  const { prevData, originMissing } = useMemo(() => {
    return (prevSettings?.data ?? []).reduce((acc, x) => {
      const id = `${x.schemaName}!${x.tableName}`
      if (candidate.allCandidates.some(c => c.id === id)) {
        acc.prevData.set(id, {...x, id} as any)
      } else {
        acc.originMissing.push(x)
      }
      return acc
    }, { 
      prevData: new Map<string, TableInputUnit>(),
      originMissing: [] as SubsetMasterTableResponseDto[],
    })
  }, [prevSettings, candidate])

  const zstore = useCreateZTreeStore()
  const utils = useZSliceUnderContext({ store: zstore,
    prevData,
    stageInit: () => ({
      added: new Map<string, TableInputUnit>(),
      removed: new Set<string>(),
    }),
    extraStates: {
      filterTerm: '',
      rowCount: '' as number|'',
      activeOption: null as TableUnit|null,
      errorHighlight: new Set<string>(),
    }
  })
  const { states, setStates, token, hooks: { useZHasDescendantChanges }, installProtocolMetadata, invokeProtocol } = utils
  const hasDescendantChange = useZHasDescendantChanges()
  const hasEdits = states.stage.removed.size > 0 || states.stage.added.size > 0 || hasDescendantChange

  type Stage = typeof utils extends FormUtils<infer T, any, any> ? T : never;
  type PrevData = typeof utils extends FormUtils<any, infer T, any> ? T : never;
  useEffect(() => {
    installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: () => Promise.resolve(),
      overrideCollect: async (node, safeInvokeNode, safeChildrenCollection) => {
        const n = node as FormNode<Stage, PrevData>
        const stage = n.state.stage
        const added = stage.added
        const prevData = n.state.prevData
        const ids = [...prevData.keys(), ...added.keys()].filter(id => !stage.removed.has(id))
        const cnodeMap = new Map<string, FormNode>(safeChildrenCollection.values().map(cnode => [cnode.state.prevData.id, cnode]))
        const payload : AddSubsetMasterTablesInput = {
          databaseId,
          masterTables: []
        }
        for (const id of ids) {
          const cnode = cnodeMap.get(id)
          if (cnode) {
            const tableEntity : TableInputUnit = await safeInvokeNode(cnode)
            payload.masterTables!.push(tableEntity)
          }
        }
        return payload
      }
    })
  }, [databaseId, installProtocolMetadata])

  const { refetch, loading } = useShowContext()
  const { enqueueSnackbar } = useSnackbar()
  const handleSave = (save: (x: any) => Promise<AddSubsetMasterTablesResponseDto>) => async () => {
    if (!hasEdits || loading) { return }
    setStates(v => ({...v, errorHighlight: new Set() }))
    const payload = await  CollectEditsProtocol.invoke<AddSubsetMasterTablesInput>(invokeProtocol)
    const result = await save(payload)
    if (result.success) {
      enqueueSnackbar('Save Master Tables Setting succeeded!', { variant: 'success' })
      refetch?.()
    } else {
      const fstErr = result.validateErrors?.[0]
      const errMsgDetail = `${fstErr?.schema}.${fstErr?.table}: ${fstErr?.message}`
      enqueueSnackbar(`Save Master Tables Setting failed: ${errMsgDetail}`, { variant: 'error' })
      const errorHighlight = new Set<string>((result.validateErrors ?? []).map(x => `${x.schema}!${x.table}`))
      setStates(v => ({...v, errorHighlight }))
    }
  }
  const [showMore, setShowMore] = useState(false)

  const handleKeyPress = (c:TableUnit) => (e:React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code !== 'Enter' && e.code !== 'NumpadEnter') { return }
    if (states.rowCount === '') { return }
    e.preventDefault()
    e.stopPropagation()
    setStates(v => {
      const newEntry = {
        id: c.id,
        databaseId,
        schemaName: c.schema as any,
        tableName: c.name as any,
        rowCount: +v.rowCount,
      }
      const v1 : typeof v = ({...v, activeOption:null, rowCount: ''})
      v1.stage.added.set(c.id, newEntry)
      v1.stage.removed.delete(c.id)
      return v1
    })
  }
  const handleRowCountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const count = Number(e.target.value)
    if (e.target.value && isNaN(count)) { return }
    setStates(v => ({...v, rowCount: count }))
  }
  const isMasterTable = (id:string) => !states.stage.removed.has(id) && (states.prevData.get(id) || states.stage.added.get(id))
  const reset = () => {
    setStates(v => ({...v,
      stage: {
        added: new Map(),
        removed: new Set(),
      }
    }))
    eventBus.publish('reset')
  }
  const displayEntries = 
    [...new Set([...states.prevData.keys(), ...states.stage.added.keys()])].sort()
      .map(id => states.prevData.get(id) ?? states.stage.added.get(id)!)
  const onDelAddBtnClick = (id: string) => {
    setStates(v => {
      const v1 = {...v}
      if (v1.stage.removed.has(id)) {
        v1.stage.removed.delete(id)
      } else if (v1.stage.added.has(id)) {
        v1.stage.added.delete(id)
      } else if (v1.prevData.has(id)) {
        v1.stage.removed.add(id)
      }
      return v1
    })
  }
  
  const [hoverHLSx, setHoverHLSx] = useState(null as any)
  const changeHoverHLSx = 
    useMemo(() => 
      debounce(
        (id: string|null) => {
          if (!id) {
            setHoverHLSx(null)
          } else {
            const id_ = id.replace('!', '__')
            setHoverHLSx({
              [`& #${id_}`]: { boxShadow: '0 0 8px 4px gold' }
            })
            document.querySelector('#'+id_)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }
        , 100)  
    , [])

  return (
    <>
      <Stack sx={{ px: 3, py: 2 }} direction="row" alignItems="center">
        <Typography variant="subtitle2">
          Master Tables Setting
        </Typography>
        <FormGroup sx={{ ml: 8, mr: 'auto' }}>
          <FormControlLabel sx={{ mr: 0 }}
            control={<Switch size="small" value={showMore} onChange={(e, checked) => setShowMore(checked)} />}
            label={
              <Typography variant="caption" color="info">Include Standalone Tables</Typography>} />
          <FormHelperText sx={{ color: 'InactiveCaptionText' }}>
            A table without any relationship with other tables is considered a standalone table.
          </FormHelperText>
        </FormGroup>
        {hasWritePermit && hasEdits &&
          <>
            {hasEdits &&
              <Button color="primary" variant="outlined" size="small"
                onClick={reset}
              >
                cancel
              </Button>}
            <WithSave async render={({ save, saving }) =>
              <Button color="primary" variant="contained" size="small" sx={{ ml: 2 }}
                disabled={saving || loading}
                onClick={handleSave(save)}
              >
                Save Change
              </Button>
            } />
          </>}
      </Stack>
      <Divider />
      <ZTreeStoreProvider value={zstore}>
        <TableUnitGroupEditorRoot className="u-group">
          <Box className="u-group-main">
            <Box className="header" id="u-group-header">
              <Stack direction="row" alignItems="center">
                <Typography variant="caption" color="InactiveCaptionText">Choose master tables:</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Input placeholder="Filter..."
                  sx={{ '& input': { ml: 0.5, fontSize: '14px' } }}
                  startAdornment={<SearchIcon fontSize="small" />}
                  value={states.filterTerm}
                  onChange={e => setStates(v => ({...v, filterTerm: e.target.value }))}
                  {...{inputProps: { "aria-label": 'Tables Filter' }}}
                />
              </Stack>
            </Box>
            <List component="div" role="listbox" aria-labelledby="u-group-header" >
              <Divider role="presentation" />
              {(showMore ? candidate.allCandidates : candidate.recommanded)
                .filter(c => !states.filterTerm || (
                  (c.schema && c.schema.toLowerCase().indexOf(states.filterTerm.toLowerCase()) > -1) ||
                  (c.name && c.name.toLowerCase().indexOf(states.filterTerm.toLowerCase()) > -1)
                ))
                .map((c, i) => (
                <ListItemButton key={c.id} divider={true} selected={states.activeOption === c} role="option"
                  onClick={(e) => {
                    if (!hasWritePermit || isMasterTable(c.id)) { return }
                    const div = e.currentTarget
                    setTimeout(() => div.querySelector('input')?.focus(), 10)
                    setStates(v => ({...v, activeOption:c, rowCount: ''}))
                  }}
                  onMouseEnter={() => changeHoverHLSx(c.id)}
                  onMouseLeave={() => changeHoverHLSx(null)}
                >
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    {isMasterTable(c.id) && <StarIcon color={states.stage.added.has(c.id) ? "success" : "info"} />}
                  </ListItemIcon>
                  <ListItemText>
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ mr: 2 }}>
                        <Typography variant="caption" color="InactiveCaptionText">{c.schema}. </Typography>
                        <Typography variant="body2">{c.name}</Typography>
                      </Box>
                      <Box sx={{ ml: 'auto' }}>
                        {hasWritePermit && states.activeOption === c
                        ? <Box sx={{ width: 100, }}>
                            <Input id="standard-basic" placeholder="Row Count" size="small" sx={{ '& input': {textAlign:'right'} }}
                              value={states.rowCount}
                              onChange={handleRowCountChange}
                              onKeyPress={handleKeyPress(c)}
                              onBlur={() => setTimeout(() => setStates(v => ({...v, activeOption:null, rowCount: ''})))}
                            />
                          </Box>
                        : <Box sx={{ '& div': { display: 'inline-block' } }}>
                            <Tooltip title="Total Row Count">
                              <Typography variant="caption" component="div" color="secondary" sx={{ mr: 1 }}>{c.totalRows != null ? numeral(c.totalRows).format('0,0') : 'n/a'}</Typography>
                            </Tooltip>
                            <Tooltip title="Upstream Table Count">
                              <Typography variant="caption" component="div" color="info">{numeral(c.upstreams ?? 0).format('0,0')}</Typography>
                            </Tooltip>
                          </Box>}
                      </Box>
                    </Stack>
                  </ListItemText>
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box className="u-group-mtable-setting" sx={hoverHLSx} >
            <Typography variant="body2" sx={{ mb: 2 }}>Master Tables: </Typography>
            <Stack direction="row" flexWrap="wrap" alignItems="start">
              {originMissing.map((x, i) => <TableUnitEntry key={i} id={i+''} missing={true} entry={x} parentToken={token} disabled={!hasWritePermit} />)}
              {displayEntries.map(entry => <TableUnitEntry key={entry.id} id={entry.id} entry={entry} parentToken={token} disabled={!hasWritePermit} 
                                                            isDel={states.stage.removed.has(entry.id)} 
                                                            isAdd={states.stage.added.has(entry.id)} 
                                                            hasErr={states.errorHighlight.has(entry.id)}
                                                            onDelAddBtnClick={onDelAddBtnClick}
                                                            />)}
            </Stack>
          </Box>
        </TableUnitGroupEditorRoot>
      </ZTreeStoreProvider>
    </>
  )
}


const DatabaseSubsetLimtEditor : FC<{databaseId: number}> = ({ databaseId }) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const { data: currDb, loading } = useGetOne<DatabaseResponseDto&RecPatch>('db-esc-status', databaseId)
  const save = useSimpleSaveAction()

  const [states, setStates] = useState({
    subsetLimit: currDb?.subsetLimit ?? 0,
    idVersion: currDb?.databaseId,
  })
  useEffect(() => {
    setStates({
      subsetLimit: currDb?.subsetLimit ?? 0,
      idVersion: currDb?.databaseId,
    })
  }, [currDb?.databaseId, currDb?.subsetLimit])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    if (isNaN(value) || value < 0 || !Number.isInteger(value)) { return }
    setStates(v => ({...v, subsetLimit: value }))
  }

  const handleSave = () => {
    if (!hasWritePermit || !currDb || states.subsetLimit === currDb.subsetLimit) { return }
    const payload : UpdateDatabaseSubsetLimitInput = {
      databaseId,
      subsetLimit: states.subsetLimit,
    }
    save(payload, {
      onSuccess: () => {
        currDb.subsetLimit = payload.subsetLimit
      }
    })
  }
  return (
    <Stack direction="row" sx={{ py: 1, px: 3, }} alignItems="center">
      <Box sx={{ width: 500 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography id="subset-threshold-lable" variant="subtitle2">Subset Threshold</Typography>
          <OutlinedInput size="small" value={states.subsetLimit} onChange={handleChange} sx={{ mr: 4 }} disabled={!hasWritePermit} {...{inputProps: { "aria-labelledby": 'subset-threshold-lable' }}} />
        </Stack>
        <Typography variant="caption" color="InactiveCaptionText" sx={{ lineHeight: '12px' }}>
          If the total number of rows in a table is less than the Subset Threshold
          , by default all rows in that table will be copied over, instead of taking a subset of rows from that table.
        </Typography>
      </Box>
      {hasWritePermit && !loading && states.idVersion === currDb?.databaseId && states.subsetLimit !== currDb?.subsetLimit &&
        <>
          <Button color="primary" variant="outlined" size="small" sx={{ ml: 'auto' }}
            onClick={() => setStates(v => ({...v, subsetLimit: currDb?.subsetLimit ?? 0}))}
          >
            cancel
          </Button>
          <Button color="primary" variant="contained" size="small" sx={{ ml: 2 }}
            disabled={states.subsetLimit === currDb?.subsetLimit}
            onClick={handleSave}
          >
            Save Change
          </Button>
        </>
      }
    </Stack>
  )
}


const xSchema = Yup.object().shape({
  rowCount: Yup.number().min(0),
  whereClause: Yup.string().nullable().when('rowCount', {
    is: 0,
    then: Yup.string().nullable().required('When Row Count is 0, a Where Clause filter must be specified.'),
    otherwise: Yup.string().nullable().optional(),
  })
})

const TableUnitEntry : FC<{
  id: string,
  parentToken: FormToken,
  entry: SubsetMasterTableResponseDto,
  missing?: boolean,
  isDel?: boolean,
  isAdd?: boolean,
  hasErr?: boolean,
  onDelAddBtnClick?: (id: string) => void,
  disabled?: boolean,
}> = ({
  id, parentToken, entry, missing,
  isDel, isAdd, hasErr, onDelAddBtnClick, disabled,
}) => {
  const eventBus = useEventBus('MasterTablesEditor')
  // State for WHERE/ORDER BY clause dialog
  const [dialogOpen, setDialogOpen] = useState(false)

  const utils = useZSliceUnderContext({ parentToken, pathFragment: id,
    prevData: entry,
    stageInit: () => ({id, ...entry} as TableInputUnit),
    extraStates: {
      tempContent: [
        ...(entry.whereClause ? ['WHERE', '  ' + entry.whereClause] : []),
        ...(entry.orderByClause ? ['ORDER BY', '  ' + entry.orderByClause] : []),
      ].join('\n'),
      lastConfirmed: undefined as TableInputUnit|undefined,
    }
  })
  const { states, setStates, installProtocolMetadata } = utils
  const { fieldProps, trySubmitValidate } = usePlainStateFormUtils(states, setStates, xSchema)

  useEffect(() => {
    installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => { await trySubmitValidate() }
    })
  }, [installProtocolMetadata, trySubmitValidate])


  const handleButtonClick = () => {
    if (disabled) { return }
    onDelAddBtnClick?.(id)
  }
  useEffect(() => {
    const subsToken = eventBus.subscribe('reset', () => {
      setStates(v => ({...v,
        prevData: entry,
        stage: {id, ...entry} as TableInputUnit,
        errors: {},
        touched: {},
        tempContent: [
          ...(entry.whereClause ? ['WHERE', '  ' + entry.whereClause] : []),
          ...(entry.orderByClause ? ['ORDER BY', '  ' + entry.orderByClause] : []),
        ].join('\n'),
        lastConfirmed: undefined as TableInputUnit|undefined,
      }))
    })
    return () => {
      eventBus.unsubscribe(subsToken)
    }
  }, [entry, eventBus, id, setStates])

  const handleOpenDialog = () => {
    setDialogOpen(true)
    setStates(v => ({...v, 
      tempContent: [
        ...(v.stage.whereClause ? ['WHERE', '  ' + v.stage.whereClause] : []),
        ...(v.stage.orderByClause ? ['ORDER BY', '  ' + v.stage.orderByClause] : []),
      ].join('\n'), 
    }))
  }

  const dialogCancelBtnRef = useRef<HTMLButtonElement>(null)
  const handleCloseDialog = () => {
    setDialogOpen(false)
    // clear unsaved edits
    setStates(v => {
      const v1 = {...v,
        stage: {...v.stage,
          whereClause: (v.lastConfirmed ?? v.prevData).whereClause,
          orderByClause: (v.lastConfirmed ?? v.prevData).orderByClause,
        },
        tempContent: '',
      }
      return v1
    })
  }

  const handleSaveClauses = () => {
    setDialogOpen(false)
    setStates(v => {
      const { whereClause, orderByClause } = parseClauses(v.tempContent);
      const stage = {...v.stage, 
        whereClause: whereClause?.trim(),
        orderByClause: orderByClause?.trim(),
      }
      const touched = {...v.touched, 
        whereClause: !v.stage.whereClause ? !!stage.whereClause : v.stage.whereClause !== stage.whereClause,
        orderByClause: !v.stage.orderByClause ? !!stage.orderByClause : v.stage.orderByClause !== stage.orderByClause,
      }
      const v1 = ({...v, lastConfirmed: stage, stage, touched, errors: {}, tempContent: '' })
      return v1  
    })
  }

  const renderClausePreview = () => {
    const sharedStyle : SxProps<Theme> = {
      fontFamily: 'monospace',
      padding: '2px 6px',
      borderRadius: '3px',
      width: '100%',
      textAlign: 'left',
      '&:focus': {
        borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.8)' : theme.palette.primary.light,
        border: '1px dashed',        
      }
    }
    const fragments = []
    if (states.errors.whereClause) {
      fragments.push(
        <FormHelperText error>{states.errors.whereClause}</FormHelperText>
      )
    }
    if (states.stage.whereClause) {
      fragments.push(
        <Typography key="where" variant="caption" component={ButtonBase} 
          sx={{
            display: 'block',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            mb: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            },
            '& > span': {
              color: theme => theme.palette.mode === 'light' ? '#ad5324' : '#dc8556'
            },
            ...sharedStyle,
          }}
          onClick={handleOpenDialog}
        >
          <span>WHERE</span> {states.stage.whereClause}
        </Typography>
      )
    }
    if (states.stage.orderByClause) {
      fragments.push(
        <Typography key="orderBy" variant="caption" component={ButtonBase} 
          sx={{
            display: 'block',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            },
            '& > span': {
              color: theme => theme.palette.mode === 'light' ? '#ad5324' : '#dc8556'
            },
            ...sharedStyle,
          }}
          onClick={handleOpenDialog}
        >
          <span>ORDER BY</span> {states.stage.orderByClause}
        </Typography>
      )
    }
    const addfilters = (
      <Typography variant="caption" component={ButtonBase} 
        sx={{
          display: 'flex',
          color: 'text.secondary',
          justifyContent: 'start',
          border: '1px dashed',
          borderColor: 'transparent',
          '& .txt': { display: 'none' },
          '& .MuiSvgIcon-root': { color: 'rgba(from currentColor r g b / 0.5)' },
          '&:hover, &:focus': {
            borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'divider',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '.txt': { display: 'inline' },
            '.MuiSvgIcon-root': { color: 'currentColor' },
          },
          ...(states.errors.whereClause && {
            borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'divider',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '.txt': { display: 'inline' },
            '.MuiSvgIcon-root': { color: 'currentColor' },
          }),
          ...sharedStyle,
        }}
        onClick={handleOpenDialog}
      >
        <FilterAltIcon sx={{ width: 16, height: 16, py: 0.25, mr: 0.5 }} />
        <span className="txt" >Add Filters</span>
      </Typography>
    )
    if (fragments.length === 0 && !isDel) {
      return addfilters
    } else if (states.errors.whereClause && !isDel) {
      fragments.push(addfilters)
    }
    return fragments
  }

  function parseClauses(s:string) {
    let content = s, whereClause = '', orderByClause = '';
    let i = /\bwhere\b/i.exec(content)?.index;
    if (i != null && i > -1) {
      whereClause = s.substring(i + 'where'.length).trimStart();
      content = whereClause;
    }
    const mArr = /\border\s+by\b/i.exec(content);
    i = mArr?.index;
    if (i != null && i > -1) {
      if (whereClause) {
        whereClause = content.substring(0, i).trimStart();
      }
      orderByClause = content.substring(i + mArr![0].length).trimStart();
    }
    return { whereClause, orderByClause };
  }
  const processTempContent = (s:string) => {
    setStates(v => {
      const stage = {...v.stage}
      return {...v, tempContent: s, stage}
    })
  }
  const hasEdit = Object.values(states.touched).some(x => x)

  return (
    <>
      <Paper id={id.replace('!', '__')} className={
          hasErr ? 'hasErr' 
        : isAdd ? 'isAdd' 
        : isDel ? 'isDel' 
        : hasEdit ? 'hasEdit' 
        : undefined
      } >
        <Typography variant="body2" className="mt-name">
          <b>
            <Typography variant="body2" component="span" color="info">{states.prevData.schemaName}.</Typography>
            {states.prevData.tableName}
          </b>
          {(missing || !disabled) &&
            <Tooltip title={
              missing ? 'This setting is obsolete due to database schema change'
              : isDel ? "Add Back" : "Remove"
            }>
              {missing
              ? <Box sx={{ ml: 'auto' }} role="tooltip"><ErrorOutlineIcon color="warning" fontSize="small" /></Box>
              : <IconButton color="secondary" sx={{ mr: -1, ml: 'auto', 
                  '&:not(:hover)': { color: 'rgba(from currentColor r g b / 0.2)' },
                }}
                  onClick={handleButtonClick}
                >
                  {isDel ? <AddIcon /> : <RemoveIcon />}
                </IconButton>}
            </Tooltip>}
        </Typography>
        {!isDel &&
          <>
            <Box sx={{ pt: 2, }}>
              <TextField fullWidth size="small" type="number"
                disabled={disabled}
                label="Target Row Count"
                {...fieldProps('rowCount')}
                value={states.stage.rowCount ?? 0}
              />
            </Box>
            <Box sx={{ pt: 1 }}>
              {renderClausePreview()}
            </Box>
          </>
        }
      </Paper>

      {/* WHERE/ORDER BY Clause Editor Dialog */}
      <Dialog maxWidth="lg" fullWidth 
        sx={{  
          '& .MuiPaper-root': {
            bgcolor: theme => theme.palette.mode === 'light' ? theme.palette.background.paper : darken(theme.palette.background.default, 0.5)
          }
        }}
        open={dialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogTitle>
          Filters for {states.prevData.schemaName}.{states.prevData.tableName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Readonly SQL preview */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              SQL Preview:
            </Typography>
            <Box sx={{
              backgroundColor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.3)',
              padding: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              whiteSpace: 'pre-line',
              mb: 2
            }}>
              <Typography
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  userSelect: 'none'
                }}
              >
                SELECT * FROM {states.prevData.tableName}
              </Typography>
              <Editor textareaId="filterSqlEditorTextarea"
                autoFocus={true}
                value={states.tempContent}
                onValueChange={processTempContent}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    e.stopPropagation()
                    dialogCancelBtnRef.current?.focus()
                  }
                }}
                highlight={highlightSQL}
                padding={10}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 14,
                  outline: 'none',
                  minHeight: '20px'
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              Enter WHERE and/or ORDER BY clauses.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme => theme.palette.mode === 'light' ? undefined : '#2b2426' }}>
          <Button onClick={handleCloseDialog} ref={dialogCancelBtnRef} >
            Cancel
          </Button>
          <Button onClick={handleSaveClauses} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const TableUnitGroupEditorRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: theme.palette.background.paper,
  '& div.u-group-main': {
    maxHeight: 'calc(100vh - 64px)',
    borderTopLeftRadius: theme.spacing(0.75),
    borderBottomLeftRadius: theme.spacing(0.75),
    borderRight: `1px solid ${theme.palette.divider}`,
    '& div.header': {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    '& div.MuiList-root': {
      paddingTop: 0,
      paddingBottom: 0,
      minWidth: 300,
      minHeight: 350,
      maxHeight: 840,
      overflowY: 'auto',
    },
  },
  '& div.u-group-mtable-setting': {
    maxHeight: 'calc(100vh - 64px)',
    overflowY: 'auto',
    flex: '1 1 auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '& div.MuiPaper-root': {
      borderRadius: '6px',
      padding: theme.spacing(1.5),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      minWidth: 210,
      border: `1px solid ${theme.palette.divider}`,
      '&.hasEdit': { border: `1px solid ${theme.palette.info.main}`, },
      '&.hasErr': { border: `1px solid ${theme.palette.error.main}`, },
      '&.isAdd': { border: `1px solid ${theme.palette.success.main}`, },
      '&.isDel': { border: `1px solid ${theme.palette.error.main}`,
        '& b': {
          textDecoration: 'line-through',
          color: theme.palette.error.main,
        }
      },

      '& .mt-name': { display: 'flex', alignItems: 'center' },
    },
  },
}))

// Objectives:
// * find table groups, and other individual tables
// * identify master table candidates for each table group, calculate priorities for candidates
//
//
// const ls : TableWithRefsResponseDto[] = [
//   { schema: 'dbo', name: '1', refs: [] },
//   { schema: 'dbo', name: '2', refs: [
//     { schema: 'dbo', name: '1', },
//     { schema: 'dbo', name: '4', },
//   ] },
//   { schema: 'dbo', name: '3', refs: [] },
//   { schema: 'dbo', name: '4', refs: [
//     { schema: 'dbo', name: '1', },
//     { schema: 'dbo', name: '5', },
//   ] },
//   { schema: 'dbo', name: '5', refs: [
//     { schema: 'dbo', name: '2', },
//     { schema: 'dbo', name: '3', },
//   ] },
//   { schema: 'dbo', name: '6', refs: [
//     { schema: 'dbo', name: '3', },
//     { schema: 'dbo', name: '6', }
//   ] },
//   { schema: 'dbo', name: '7', refs: [] },
//   { schema: 'dbo', name: '8', refs: [] },
// ]

const getMasterTableCandidates = (ls: TableWithRefsResponseDto[]) : CalculatedCandidates => {
  const result = { recommanded: [] as TableUnit[], allCandidates: [] as TableUnit[] }

  const groups = getUnitGroups(ls)
  groups.forEach(g => {
    if (g.candidates.length === 0) {
      const units = Object.values(g.units)
      if (units.length === 1) {
        result.allCandidates.push(Object.values(g.units)[0])
      } else if (units.length > 1) {
        units.sort((a, b) =>
          (a.upstreams ?? 0) === (b.upstreams ?? 0) ? (a.id < b.id ?  -1 : 1)
          : (a.upstreams ?? 0) > (b.upstreams ?? 0) ? -1 : 1)
        if (units[0].upstreams === units[1].upstreams) {
          result.allCandidates.push(...units)
        } else {
          result.recommanded.push(units[0])
        }
      }
    } else {
      result.recommanded.push(...g.candidates)
      result.allCandidates.push(...g.candidates)
    }
  })
  result.recommanded = result.recommanded.sort((a, b) => a.id < b.id ? -1 : 1)
  result.allCandidates = result.allCandidates.sort((a, b) => a.id < b.id ? -1 : 1)
  return result
}

interface CalculatedCandidates {
  recommanded: TableUnit[],
  allCandidates: TableUnit[],
}

const getUnitGroups = (ls : TableWithRefsResponseDto[]) => {
  const allUnits : Record<string, TableUnit> = {}
  // directed graph - flow from Foreign to Primary
  const gF2P = ls.reduce((acc, x) => {
    const key = `${x.schema}!${x.name}`
    allUnits[key] = { id: key, schema: x.schema, name: x.name, totalRows: x.totalRows }
    acc[key] = (x.refs || []).map(r => {
      const u = ({ id: `${r.schema}!${r.name}`, ...r })
      allUnits[u.id] = u
      return u
    })
    return acc
  }, {} as Record<string, TableUnit[]>)

  // directed graph - flow from Primary to Foreign
  const gP2F = Object.keys(gF2P).reduce((acc, pId) => {
    const p = allUnits[pId]
    const fs = gF2P[pId] || []
    for(const f of fs) {
      const arr = acc[f.id] || (acc[f.id] = [])
      arr.push(p)
    }
    return acc
  }, {} as Record<string, TableUnit[]>)

  const visited = new Set<string>()
  const unitGroups = Object.keys(allUnits).reduce((acc, id) => {
    if (visited.has(id)) { return acc }
    const group : UnitGroup = { units: {}, travelStack: [], candidates: [], accessableCounts: {}, }
    const visit = (u: TableUnit) => {
      visited.add(u.id)
      group.units[u.id] = u
      const pfOuts = gP2F[u.id] ?? []
      const fpOuts = gF2P[u.id] ?? []
      group.travelStack.push(...pfOuts, ...fpOuts)
      if (pfOuts.length === 0 && fpOuts.length > 0) {
        group.candidates.push(u)
      }
    }
    const start = allUnits[id]
    visit(start)

    while (group.travelStack.length > 0) {
      const u = group.travelStack.pop()
      if (u && !visited.has(u.id)) {
        visit(u)
      }
    }

    // calculate accessableCounts for candidates
    group.candidates.forEach(c => {
      const visitedInner = new Set<string>()
      const visitInner = (u:TableUnit) => {
        visitedInner.add(u.id)
        group.travelStack.push(...(gF2P[u.id] ?? []))
        group.accessableCounts[c.id] = (group.accessableCounts[c.id] ?? 0) + 1
      }
      visitInner(c)
      while (group.travelStack.length > 0) {
        const u = group.travelStack.pop()
        if (u && !visitedInner.has(u.id)) {
          visitInner(u)
        }
      }
      c.upstreams = group.accessableCounts[c.id] != null
        ? group.accessableCounts[c.id] - 1
        : 0
    })
    group.candidates.sort((a, b) => group.accessableCounts[b.id] - group.accessableCounts[a.id])

    acc.push(group)
    return acc
  }, [] as UnitGroup[])

  return unitGroups
}

interface TableUnit extends TableSchemaAndNameDto {
  id: string,
  upstreams?: number,
}

interface TableInputUnit extends SubsetMasterTableInput {
  id: string
}

interface UnitGroup {
  units: Record<string, TableUnit>,
  travelStack: TableUnit[],
  candidates: TableUnit[],
  accessableCounts: Record<string, number>,
}

const highlightSQL = (code: string) => {
  return Prism.highlight(code, Prism.languages.sql, 'sql');
};
